import React, { createContext, useContext, useState, useEffect } from "react";
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  setDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebase";
import { UserAuth } from "./AuthContext";

const PasswordContext = createContext();

export function PasswordProvider({ children }) {
  const [authorizedSeries, setAuthorizedSeries] = useState([]); // Stores objects { seriesId, password }
  const [passwordModal, setPasswordModal] = useState({
    isOpen: false,
    seriesId: null,
    seriesTitle: null,
    redirectUrl: null,
  });
  const [passwordError, setPasswordError] = useState("");
  const [loading, setLoading] = useState(true);
  const { user } = UserAuth();

  // Load authorized series from localStorage or Firestore on mount
  useEffect(() => {
    const loadAuthorizedSeries = async () => {
      if (user?.uid) {
        // If the user is logged in, fetch the authorizedSeries from Firestore
        try {
          const userDocRef = doc(db, "users", user.uid);
          const userDoc = await getDoc(userDocRef);

          if (userDoc.exists() && userDoc.data().authorizedSeries) {
            const firestoreAuthorizedSeries =
              userDoc.data().authorizedSeries || [];
            setAuthorizedSeries(firestoreAuthorizedSeries);
          } else {
            // If no document or authorizedSeries in Firestore, initialize with an empty array
            await setDoc(
              userDocRef,
              {
                authorizedSeries: [],
                email: user.email,
                updatedAt: new Date(),
              },
              { merge: true }
            );
            setAuthorizedSeries([]);
          }
        } catch (error) {
          console.error(
            "Error loading authorized series from Firestore:",
            error
          );
        }
      } else {
        // If the user is not logged in, use localStorage for storing authorizedSeries
        const localStorageAuthorized =
          JSON.parse(localStorage.getItem("authorizedSeries")) || [];
        setAuthorizedSeries(localStorageAuthorized);
      }
      setLoading(false);
    };

    loadAuthorizedSeries();
  }, [user?.uid]); // Only run when the user's uid changes

  // Sync authorizedSeries between localStorage and Firestore when user logs in
  useEffect(() => {
    if (user?.uid && !loading) {
      // When user logs in, merge localStorage authorizations with Firestore
      const localStorageAuthorized =
        JSON.parse(localStorage.getItem("authorizedSeries")) || [];

      if (localStorageAuthorized.length > 0) {
        const mergedSeries = [...authorizedSeries];
        let hasNewEntries = false;

        // Add any local entries not in Firestore
        localStorageAuthorized.forEach((localEntry) => {
          const exists = mergedSeries.some(
            (fsEntry) => fsEntry.seriesId === localEntry.seriesId
          );

          if (!exists) {
            mergedSeries.push(localEntry);
            hasNewEntries = true;
          }
        });

        // Update Firestore if we have new entries
        if (hasNewEntries) {
          const userDocRef = doc(db, "users", user.uid);
          updateDoc(userDocRef, {
            authorizedSeries: mergedSeries,
            updatedAt: new Date(),
          }).catch((err) =>
            console.error("Error syncing authorized series:", err)
          );

          setAuthorizedSeries(mergedSeries);
        }
      }
    }
  }, [user?.uid, loading, authorizedSeries]);

  // Function to check if a series requires a password and if the user has access
  // Modified to support the checkSeriesAccess API expected by Row component
  const checkSeriesAccess = async (seriesId, redirectUrl, showModal = true) => {
    if (!seriesId) return { isAuthorized: true, isProtected: false };

    // Wait for authorized series to be loaded
    if (loading) {
      return { isAuthorized: false, isProtected: true };
    }

    try {
      // Get series data directly from Firestore instead of context
      const seriesRef = collection(db, "content", "main", "series");
      const seriesQuery = query(seriesRef, where("id", "==", seriesId));
      const seriesSnapshot = await getDocs(seriesQuery);

      if (seriesSnapshot.empty) {
        return {
          isAuthorized: true,
          isProtected: false,
          error: "Series not found",
        };
      }

      const series = seriesSnapshot.docs[0].data();

      if (!series.password) {
        return { isAuthorized: true, isProtected: false };
      }

      // Check if already authorized with valid password
      const existingAuth = authorizedSeries.find(
        (item) =>
          item.seriesId === seriesId && item.password === series.password
      );

      if (existingAuth) {
        return { isAuthorized: true, isProtected: true };
      }

      // Only show modal if explicitly requested and not already authorized
      if (showModal && !existingAuth) {
        setPasswordModal({
          isOpen: true,
          seriesId,
          seriesTitle: series.title,
          redirectUrl,
        });
      }

      return { isAuthorized: false, isProtected: true };
    } catch (error) {
      console.error("Error checking series access:", error);
      return { isAuthorized: false, isProtected: false, error: error.message };
    }
  };

  // Function to check if a user has access to a specific series
  const hasAccess = async (seriesId) => {
    if (!seriesId) return true;

    try {
      const seriesRef = collection(db, "content", "main", "series");
      const seriesQuery = query(seriesRef, where("id", "==", seriesId));
      const seriesSnapshot = await getDocs(seriesQuery);

      if (seriesSnapshot.empty || !seriesSnapshot.docs[0].data().password) {
        return true;
      }

      const series = seriesSnapshot.docs[0].data();
      const password = series.password;

      // Check if the seriesId and password pair exists in authorizedSeries
      return authorizedSeries.some(
        (item) => item.seriesId === seriesId && item.password === password
      );
    } catch (error) {
      console.error("Error checking series access:", error);
      return false;
    }
  };

  const verifyPassword = async (password) => {
    const { seriesId } = passwordModal;
    setPasswordError("");

    try {
      const seriesRef = collection(db, "content", "main", "series");
      const seriesQuery = query(seriesRef, where("id", "==", seriesId));
      const seriesSnapshot = await getDocs(seriesQuery);

      if (seriesSnapshot.empty) {
        setPasswordError("Series not found");
        return false;
      }

      const seriesData = seriesSnapshot.docs[0].data();
      const correctPassword = seriesData.password;

      if (password === correctPassword) {
        // Check if we already have this authorization to prevent duplicates
        const existingAuth = authorizedSeries.find(
          (item) => item.seriesId === seriesId && item.password === password
        );

        if (!existingAuth) {
          setAuthorizedSeries((prev) => {
            const updatedSeries = [...prev, { seriesId, password }];

            if (!user?.uid) {
              localStorage.setItem(
                "authorizedSeries",
                JSON.stringify(updatedSeries)
              );
            } else {
              const userDocRef = doc(db, "users", user.uid);
              updateDoc(userDocRef, {
                authorizedSeries: arrayUnion({ seriesId, password }),
                updatedAt: new Date(),
              }).catch((err) =>
                console.error("Error updating authorized series:", err)
              );
            }

            return updatedSeries;
          });
        }

        setPasswordModal((prev) => ({ ...prev, isOpen: false }));
        return true;
      } else {
        setPasswordError("Incorrect password");
        return false;
      }
    } catch (error) {
      console.error("Error verifying password:", error);
      setPasswordError("Error verifying password");
      return false;
    }
  };

  const closePasswordModal = () => {
    setPasswordModal({
      isOpen: false,
      seriesId: null,
      seriesTitle: null,
      redirectUrl: null,
    });
    setPasswordError("");
  };

  // Handle user logout - clear localStorage if needed
  const clearLocalAuthorizations = () => {
    localStorage.removeItem("authorizedSeries");
    setAuthorizedSeries([]);
  };

  const value = {
    authorizedSeries,
    passwordModal,
    passwordError,
    loading,
    setAuthorizedSeries,
    checkSeriesAccess,
    hasAccess,
    verifyPassword,
    closePasswordModal,
    clearLocalAuthorizations,
  };

  return (
    <PasswordContext.Provider value={value}>
      {children}
    </PasswordContext.Provider>
  );
}

export function usePassword() {
  return useContext(PasswordContext);
}
